import { ReportWebService, RestfulReportWebService } from 'ws/ReportWebService';
import { FinalReportResponseStatus } from 'core/finalReport/FinalReportStatus';
import { Order } from 'core/order/Order';
import { isNil, isUndefined, omitBy } from 'lodash';
import {
  OutdoorDeviceMetricType,
  OutdoorDeviceReportData,
  ReportCompareData,
  ReportCompareDimension,
  ReportData,
  ReportDimension,
  ReportGran,
  ReportType
} from './ReportData';
import i18n from 'i18n';
import config from 'config';
import { FinalReportType } from 'core/finalReport/FinalReport';
import {
  WeatherVerificationCampaignOption,
  WeatherVerificationData,
  WeatherVerificationQuery,
  WeatherVerificationStoreOption
} from './WeatherVerification';
import {
  DeliveryBriefFormat,
  DeliveryBriefMetric
} from './DeliveryBriefReport';

export const MRT_OPTIONS = [
  {
    label: i18n.t('limitation.locations.xiangshanStation'),
    value: config.env === 'live' ? '58388,59300,60160' : '2579'
  },
  {
    label: i18n.t('limitation.locations.daanStation'),
    value: config.env === 'live' ? '58387,59299,60159' : '2587'
  },
  {
    label: i18n.t('limitation.locations.nanjingFuxingStation'),
    value: config.env === 'live' ? '58389,59301,60161' : '2662'
  },
  {
    label: i18n.t('limitation.locations.songshanStation'),
    value: config.env === 'live' ? '58390,59302,60162' : '2663'
  },
  {
    label: i18n.t('頤曜'),
    value: config.env === 'live' ? '2018092' : '2663'
  },
  {
    label: i18n.t('limitation.locations.shanbStation'),
    value: config.env === 'live' ? '2018093' : '2663'
  }
];

export interface ReportManager {
  downloadOrderDeliveryBriefReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    format: DeliveryBriefFormat,
    metrics: DeliveryBriefMetric[]
  ): Promise<void>;
  downloadOrderReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void>;
  downloadL1ObjectReport(
    order: {
      name: string;
      l1ObjectId: number;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void>;
  downloadVideoReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadOutdoorReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadClosingReport(order: Order, reportType?: string): Promise<void>;
  downloadRebateReport(
    date: string,
    agencyName: string,
    agencyId?: string | number
  ): Promise<void>;
  getReportData(
    type: ReportType,
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string,
    useAppsflyerConversion?: boolean
  ): Promise<ReportData>;
  getReportCompareData(
    compareDimension: ReportCompareDimension,
    filter: any,
    from: string,
    to: string
  ): Promise<ReportCompareData>;
  uploadFinalReportData(
    orderId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus>;
  getFinalReportData(
    reportId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus>;
  downloadOrderReportRawData(
    orderId: string | number,
    startDate: string,
    endDate: string
  ): Promise<void>;
  getFilterOptions(
    type: ReportType,
    gran: ReportGran,
    dimension: string,
    currentFilters: {
      [key: string]: string | number;
    }
  ): Promise<SelectOptions[]>;
  downloadSalesChannelReconciliationReport(
    gran: ReportGran,
    salesChannelId: number,
    from: string,
    to: string
  ): Promise<void>;
  downloadOrderOutdoorDeviceReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadOrderOutdoorDeviceBeaconReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadOrderBeaconEventReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void>;
  getOutdoorDeviceReportData(
    l1ObjectId: number,
    from: string,
    to: string,
    metricType: OutdoorDeviceMetricType,
    gran: ReportGran
  ): Promise<OutdoorDeviceReportData>;
  getWeatherVerificationCampaigns(
    l1ObjectId: number,
    from: string,
    to: string
  ): Promise<WeatherVerificationCampaignOption[]>;
  getWeatherVerificationStores(
    l1ObjectId: number,
    campaignId: number,
    from: string,
    to: string
  ): Promise<WeatherVerificationStoreOption[]>;
  getWeatherVerificationData(
    query: WeatherVerificationQuery
  ): Promise<WeatherVerificationData>;
}

export class DefaultReportManager implements ReportManager {
  webService: ReportWebService;

  constructor(webService: ReportWebService = new RestfulReportWebService()) {
    this.webService = webService;
  }
  async downloadOrderReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void> {
    await this.webService.downloadOrderReport(
      order,
      startDate,
      endDate,
      groupBy,
      selectFields
    );
  }
  async downloadOrderDeliveryBriefReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    format: DeliveryBriefFormat,
    metrics: DeliveryBriefMetric[]
  ): Promise<void> {
    await this.webService.downloadOrderDeliveryBriefReport(
      order,
      startDate,
      endDate,
      format,
      metrics
    );
  }
  async downloadL1ObjectReport(
    l1Object: {
      name: string;
      l1ObjectId: number;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void> {
    await this.webService.downloadL1ObjectReport(
      l1Object,
      startDate,
      endDate,
      groupBy,
      selectFields
    );
  }
  async downloadVideoReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void> {
    await this.webService.downloadVideoReport(order, startDate, endDate);
  }
  async downloadOutdoorReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void> {
    await this.webService.downloadOutdoorReport(order, startDate, endDate);
  }
  async downloadClosingReport(
    order: Order,
    reportType?: string
  ): Promise<void> {
    await this.webService.downloadClosingReport(order, reportType);
  }
  async downloadRebateReport(
    date: string,
    agencyName: string,
    agencyId?: string | number
  ): Promise<void> {
    await this.webService.downloadRebateReport(date, agencyName, agencyId);
  }
  async getOutdoorReportData(
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string
  ) {
    let filterToServer = {
      ...filter
    };
    // 將 MRT filter 轉成 l2ObjectId 再給 server
    let mrtFilterConflictWithL2ObjectFilter = false;
    if (ReportDimension.MRT in filter) {
      let targetL2Objects = MRT_OPTIONS.find(
        (option) => option.label === filter[ReportDimension.MRT]
      )?.value;
      if (ReportDimension.L2_OBJECT in filter) {
        // 如果有 l2Object 的 filter，取 MRT 轉換後的 l2ObjectId 跟原本 filter 的 l2ObjectId 做交集
        const mrtL2Objects = targetL2Objects ? targetL2Objects.split(',') : [];
        const originalL2Object = filter[ReportDimension.L2_OBJECT];
        targetL2Objects = mrtL2Objects.includes(originalL2Object)
          ? originalL2Object
          : undefined;
        if (!targetL2Objects) {
          mrtFilterConflictWithL2ObjectFilter = true;
        }
      }
      filterToServer = omitBy(
        {
          ...filter,
          [ReportDimension.MRT]: undefined,
          [ReportDimension.L2_OBJECT]: targetL2Objects
        },
        isUndefined
      );
    }
    // 將 MRT dimension 轉成 l2ObjectId dimension 再給 server
    let dimensionToServer =
      dimension === ReportDimension.MRT ? ReportDimension.L2_OBJECT : dimension;
    const outdoorReportData = await this.webService.getOutdoorReportData(
      dimensionToServer,
      gran,
      filterToServer,
      from,
      to
    );
    // 如果 filter 中 MRT 跟 l2ObjectId 選項同時存在，且不是相同 l2ObjectId，回傳空資料
    if (mrtFilterConflictWithL2ObjectFilter) {
      outdoorReportData.records = [];
      return outdoorReportData;
    }
    // 如果 dimension 是 MRT，則過濾掉不在 MRT 對應 campaignIds 的紀錄
    if (dimension === ReportDimension.MRT) {
      const l2ObjectIdOptionMap = MRT_OPTIONS.reduce(
        (acc, option) => {
          const ids = option.value.toString().split(',');
          ids.forEach((id) => {
            acc[id] = option.label;
          });
          return acc;
        },
        {} as { [key: string]: string }
      );
      const l2ObjectIds = Object.keys(l2ObjectIdOptionMap);
      outdoorReportData.records = outdoorReportData.records.filter((record) => {
        return l2ObjectIds.includes(record.dimensionID!.toString());
      });
      outdoorReportData.records.forEach((record) => {
        const dimensionName =
          l2ObjectIdOptionMap[record.dimensionID!.toString()];
        // 將 dimensionID 跟 dimensionName 轉換成對應的 MRT 資訊
        record.dimensionID = dimensionName;
        record.dimensionName = dimensionName ?? record.dimensionName;
      });
      // 將相同 dimensionName 的資料合併
      const mergedRecords: { [key: string]: any } = {};
      outdoorReportData.records.forEach((record) => {
        const key = record.dimensionName;
        if (!mergedRecords[key]) {
          mergedRecords[key] = { ...record };
        } else {
          // 合併數值欄位
          Object.keys(record).forEach((field) => {
            if (
              typeof record[field] === 'number' &&
              !['parentId', 'agcProfit', 'sysProfit'].includes(field)
            ) {
              mergedRecords[key][field] += record[field];
            }
          });
        }
      });
      outdoorReportData.records = Object.values(mergedRecords);
    }
    return outdoorReportData;
  }
  async getReportData(
    type: ReportType,
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string,
    useAppsflyerConversion?: boolean
  ) {
    if (type === ReportType.OUTDOOR) {
      return this.getOutdoorReportData(dimension, gran, filter, from, to);
    }
    return this.webService.getReportData(
      type,
      dimension,
      gran,
      filter,
      from,
      to,
      useAppsflyerConversion
    );
  }
  async getReportCompareData(
    compareDimension: ReportCompareDimension,
    filter: any,
    from: string,
    to: string
  ): Promise<ReportCompareData> {
    return this.webService.getReportCompareData(
      compareDimension,
      filter,
      from,
      to
    );
  }
  async uploadFinalReportData(
    orderId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus> {
    return this.webService.uploadFinalReportData(orderId, reportType);
  }
  async getFinalReportData(
    reportId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus> {
    return this.webService.getFinalReportData(reportId, reportType);
  }

  async downloadOrderReportRawData(
    orderId: string | number,
    startDate: string,
    endDate: string
  ): Promise<void> {
    return this.webService.downloadOrderReportRawData(
      orderId,
      startDate,
      endDate
    );
  }

  async downloadOrderOutdoorDeviceReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void> {
    return this.webService.downloadOrderOutdoorDeviceReport(
      orderNumber,
      startDate,
      endDate
    );
  }

  async downloadOrderOutdoorDeviceBeaconReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void> {
    return this.webService.downloadOrderOutdoorDeviceBeaconReport(
      orderNumber,
      startDate,
      endDate
    );
  }

  async downloadOrderBeaconEventReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void> {
    return this.webService.downloadOrderBeaconEventReport(
      orderNumber,
      startDate,
      endDate
    );
  }

  async getFilterOptions(
    type: ReportType,
    gran: ReportGran,
    dimension: string,
    currentFilters: {
      [key: string]: string | number;
    }
  ): Promise<SelectOptions[]> {
    let queryType;
    switch (type) {
      case ReportType.VIDEO:
        queryType = 'VIDEO';
        break;
      case ReportType.LINE_MESSAGE:
        queryType = 'LINE_MESSAGE';
        break;
      default:
        queryType = 'PERFORMANCE';
        break;
    }
    return this.webService.getFilterOptions(
      queryType,
      gran === ReportGran.HOUR ? 'hour' : 'daily',
      dimension,
      omitBy(currentFilters, isNil)
    );
  }

  async downloadSalesChannelReconciliationReport(
    gran: ReportGran,
    salesChannelId: number,
    startDate: string,
    endDate: string
  ): Promise<void> {
    return this.webService.downloadSalesChannelReconciliationReport(
      gran,
      salesChannelId,
      startDate,
      endDate
    );
  }

  async getOutdoorDeviceReportData(
    l1ObjectId: number,
    from: string,
    to: string,
    metricType: OutdoorDeviceMetricType,
    gran: ReportGran
  ): Promise<OutdoorDeviceReportData> {
    return this.webService.getOutdoorDeviceReportData(
      l1ObjectId,
      from,
      to,
      metricType,
      gran
    );
  }

  async getWeatherVerificationCampaigns(
    l1ObjectId: number,
    from: string,
    to: string
  ): Promise<WeatherVerificationCampaignOption[]> {
    return this.webService.getWeatherVerificationCampaigns(
      l1ObjectId,
      from,
      to
    );
  }

  async getWeatherVerificationStores(
    l1ObjectId: number,
    campaignId: number,
    from: string,
    to: string
  ): Promise<WeatherVerificationStoreOption[]> {
    return this.webService.getWeatherVerificationStores(
      l1ObjectId,
      campaignId,
      from,
      to
    );
  }

  async getWeatherVerificationData(
    query: WeatherVerificationQuery
  ): Promise<WeatherVerificationData> {
    return this.webService.getWeatherVerificationData(query);
  }
}
